import { useEffect, useMemo, useState } from 'react';
import useLayoutConfigSetting from 'src/hooks/useLayoutConfigSetting';

type CachedOrderbookRowHoverStateDisplayProps = {
	rowsSectionWrapperBoundingClientRect: DOMRect | null;
	buySideWrapperBoundingClientRect: DOMRect | null;
	sellSideWrapperBoundingClientRect: DOMRect | null;
	rowsSectionScrollOffset: number;
};

/**
 * This hook should wrap all the complexity around gathering the necessary props to render the hover content for the orderbook rows in an efficient way.
 *
 * Key considerations taken into account: (see here for why we've done the following : [Github :: What forces layout / reflow](https://gist.github.com/paulirish/5d52fb081b3570c81e3a))
 * - Minimising calls to getBoundingClientRect
 * -- Instead of calling it at render time, we only call it when the component mounts and when events fire which might require recalculating the bounding rects
 * - Minimising READING of element display properties (these can also cause reflows apparently)
 * -- Instead of passing the refs to some of the necessary elements, which then would get read at render time, we pass the pre-fetched bounding rects as props
 */
export const useOrderbookRowHoverStateDisplayProps = (
	buySideRowCount: number,
	sellSideRowCount: number,
	rowsSectionWrapperRef: React.RefObject<HTMLDivElement>,
	buySideWrapperRef: React.RefObject<HTMLDivElement>,
	sellSideWrapperRef: React.RefObject<HTMLDivElement>
): CachedOrderbookRowHoverStateDisplayProps => {
	// Cache the bounding client rect and only update when needed
	const [
		rowsSectionWrapperBoundingClientRect,
		setRowsSectionWrapperBoundingClientRect,
	] = useState<DOMRect | null>(null);

	const [
		buySideWrapperBoundingClientRect,
		setBuySideWrapperBoundingClientRect,
	] = useState<DOMRect | null>(null);

	const [
		sellSideWrapperBoundingClientRect,
		setSellSideWrapperBoundingClientRect,
	] = useState<DOMRect | null>(null);

	// Sync sell-side bounding rect with the number of sell-side rows
	useEffect(() => {
		if (sellSideWrapperRef.current) {
			setSellSideWrapperBoundingClientRect(
				sellSideWrapperRef.current.getBoundingClientRect()
			);
		}
	}, [sellSideRowCount]);

	// Sync buy-side bounding rect with the number of buy-side rows
	useEffect(() => {
		if (buySideWrapperRef.current) {
			setBuySideWrapperBoundingClientRect(
				buySideWrapperRef.current.getBoundingClientRect()
			);
		}
	}, [buySideRowCount]);

	const [layoutConfig] = useLayoutConfigSetting();

	// Initialize the bounding rects and sync with resize and scroll events
	useEffect(() => {
		if (
			!rowsSectionWrapperRef.current ||
			!buySideWrapperRef.current ||
			!sellSideWrapperRef.current
		) {
			return;
		}

		const updateBoundingRects = () => {
			const rowsSectionRect =
				rowsSectionWrapperRef.current?.getBoundingClientRect() ?? null;
			setRowsSectionWrapperBoundingClientRect(rowsSectionRect);

			const buySideRect =
				buySideWrapperRef.current?.getBoundingClientRect() ?? null;
			setBuySideWrapperBoundingClientRect(buySideRect);

			const sellSideRect =
				sellSideWrapperRef.current?.getBoundingClientRect() ?? null;
			setSellSideWrapperBoundingClientRect(sellSideRect);
		};

		updateBoundingRects();

		// Re-check the bounding rects on resize events
		window.addEventListener('resize', updateBoundingRects);

		const pageContentScrollWrapper = document.getElementById(
			'nav-footer-wrapper-middle-content-scroll-wrapper'
		);

		if (pageContentScrollWrapper) {
			pageContentScrollWrapper.addEventListener('scroll', updateBoundingRects);
		}

		// Cleanup the interval and event listeners
		return () => {
			window.removeEventListener('resize', updateBoundingRects);
			if (pageContentScrollWrapper) {
				pageContentScrollWrapper.removeEventListener(
					'scroll',
					updateBoundingRects
				);
			}
		};
	}, [
		layoutConfig,
		rowsSectionWrapperRef.current,
		buySideWrapperRef.current,
		sellSideWrapperRef.current,
	]);

	// This is necessary to to position the hover content relative to the scroll position of the orderbook (see OrderbookRowHoverContent for more details).
	//// We calculate it here because the value will be the same for every orderbook row. Each of these parameter accesses is liable to trigger a reflow (see link in comment above) so by doing this calculation HERE we amortize the cost across all of the rows
	const rowsSectionScrollOffset = rowsSectionWrapperRef.current
		? rowsSectionWrapperRef.current?.scrollHeight -
			(rowsSectionWrapperRef.current.clientHeight -
				rowsSectionWrapperRef.current?.scrollTop)
		: 0;

	const result = useMemo(() => {
		return {
			rowsSectionScrollOffset,
			rowsSectionWrapperBoundingClientRect,
			buySideWrapperBoundingClientRect,
			sellSideWrapperBoundingClientRect,
		};
	}, [
		rowsSectionWrapperBoundingClientRect,
		buySideWrapperBoundingClientRect,
		sellSideWrapperBoundingClientRect,
	]);

	return result;
};
